import { Container, Graphics } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W } from '../app/theme';
import { Button, makeText, sceneBackground, glassCard, Panel } from '../ui/widgets';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import { TicketReferenceField, TicketReferencePicker } from '../ui/TicketReferencePicker';
import { viewport } from '../app/viewport';
import { pageHeader, fieldLabel, bottomBar, enterFade } from './common';
import type { AppContext } from '../app/AppContext';
import { RoomName, TicketStatus, TicketType } from '../net/enums';
import { MessageTag } from '../net/messageTags';
import { encodeTicketRequest, encodeTicketSave, decodeTicket, type TicketState } from '../net/codec/ticket';
import {
  ticketReferenceOptions,
  validateTicketDraft,
  type TicketReferenceOption,
} from './ticketReferences';
import { computeTicketMessageLayout } from './ticketLayout';

const CATEGORY_OPTIONS: Array<{ type: TicketType; label: string; icon: string }> = [
  { type: TicketType.General, label: 'عمومی', icon: 'support' },
  { type: TicketType.Game, label: 'بازی', icon: 'play' },
  { type: TicketType.Transaction, label: 'تراکنش', icon: 'credit-card' },
  { type: TicketType.Payback, label: 'برداشت', icon: 'wallet' },
];

const STATUS_LABELS: Record<number, string> = {
  [TicketStatus.New]: 'خوانده نشده',
  [TicketStatus.Readed]: 'خوانده شده',
  [TicketStatus.InProgress]: 'در حال بررسی',
  [TicketStatus.Done]: 'پاسخ داده شده',
  [TicketStatus.Offensive]: 'رد شده',
};

/**
 * Touch-first support composer. Category choices are large tiles and non-general categories use a
 * dedicated full-screen reference picker instead of a long dropdown above the mobile keyboard.
 */
export class TicketScene extends Scene {
  private state: TicketState | null = null;
  private readonly body = new Container();
  private entered = false;

  private subjectField?: TextField;
  private bodyField?: TextArea;
  private selectedType = TicketType.General;
  private selectedReference: TicketReferenceOption | null = null;
  private referencePicker?: TicketReferencePicker;
  private formDialogOpen = false;

  constructor(private readonly ctx: AppContext) {
    super();
  }

  onEnter(): void {
    this.addChild(sceneBackground());
    this.addChild(this.body);
    this.buildLoading();

    this.track(
      this.ctx.client.on(MessageTag.Ticket, (r) => {
        this.state = decodeTicket(r);
        this.build();
      }),
    );
    this.ctx.client.send(encodeTicketRequest());
  }

  /** Preserve the draft and rebuild against Telegram's live portrait height. */
  onResize(): void {
    const reopenReferencePicker = Boolean(this.referencePicker);
    this.closeReferencePicker();
    if (this.state) this.build();
    else this.buildLoading();
    if (reopenReferencePicker) this.openReferencePicker();
    else if (this.formDialogOpen) this.body.visible = false;
  }

  private buildLoading(): void {
    this.closeReferencePicker();
    this.destroyBody();
    this.body.addChild(pageHeader('پشتیبانی', () => this.ctx.navigate(RoomName.MainMenu), this.ctx.iconTexture('support')));

    const left = 40;
    const w = DESIGN_W - 80;
    const y = Math.max(150, (viewport.h - 180) / 2);
    this.body.addChild(glassCard(left, y, w, 150));
    const loading = makeText('در حال دریافت اطلاعات پشتیبانی…', {
      size: 25,
      fill: COLORS.textDim,
      align: 'center',
    });
    loading.anchor.set(0.5);
    loading.position.set(DESIGN_W / 2, y + 75);
    this.body.addChild(loading);
  }

  private build(): void {
    const state = this.state;
    if (!state) return;

    // A server re-send or resize must not erase edits already typed into the live form.
    const existing = state.status !== TicketStatus.Empty;
    const subjectValue = this.subjectField?.value ?? (existing ? state.subject : '');
    const bodyValue = this.bodyField?.value ?? (existing ? state.body : '');
    this.closeReferencePicker();
    this.destroyBody();
    this.body.visible = true;
    this.body.alpha = 1;

    const left = 40;
    const w = DESIGN_W - 80;
    const header = pageHeader('پشتیبانی', () => this.ctx.navigate(RoomName.MainMenu), this.ctx.iconTexture('support'));
    this.body.addChild(header);

    let y = 112;
    const animatedNodes: Container[] = [header];

    if (existing) {
      const status = this.statusCard(state.status, w);
      status.position.set(left, y);
      this.body.addChild(status);
      animatedNodes.push(status);
      y += 76;

      if (state.answer) {
        const answer = new Button('مشاهده پاسخ پشتیبانی', () => {
          this.subjectField?.blur();
          this.bodyField?.blur();
          this.showFormDialog({ title: 'پاسخ پشتیبانی', message: state.answer });
        }, {
          width: w,
          height: 68,
          fontSize: 25,
          fill: COLORS.panelSoft,
          textColor: COLORS.text,
          icon: this.ctx.iconTexture('support'),
          accessibleLabel: 'مشاهده پاسخ کامل پشتیبانی',
        });
        answer.position.set(left, y);
        this.body.addChild(answer);
        animatedNodes.push(answer);
        y += 84;
      }
    }

    const formTop = y;
    const form = new Container();

    form.addChild(fieldLabel('دسته‌بندی', DESIGN_W - 58, y));
    const categoryY = y + 36;
    const categoryGap = 12;
    const categoryW = (w - categoryGap) / 2;
    const categoryH = 72;
    CATEGORY_OPTIONS.forEach((category, index) => {
      const selected = category.type === this.selectedType;
      const col = index % 2;
      const row = Math.floor(index / 2);
      const button = new Button(category.label, () => this.chooseType(category.type), {
        width: categoryW,
        height: categoryH,
        fontSize: 25,
        fill: selected ? COLORS.accent : COLORS.panelSoft,
        textColor: selected ? undefined : COLORS.text,
        icon: this.ctx.iconTexture(category.icon),
        accessibleLabel: `دسته‌بندی ${category.label}${selected ? '، انتخاب شده' : ''}`,
      });
      // General/Transaction lead on the right in the RTL two-column grid.
      button.position.set(left + (1 - col) * (categoryW + categoryGap), categoryY + row * (categoryH + categoryGap));
      form.addChild(button);
    });
    y = categoryY + categoryH * 2 + categoryGap + 20;

    if (this.selectedType !== TicketType.General) {
      const references = ticketReferenceOptions(state, this.selectedType);
      const currentReference = this.selectedReference;
      if (currentReference && !references.some((option) => this.sameReference(option, currentReference))) {
        this.selectedReference = null;
      }

      form.addChild(fieldLabel('مورد مرتبط', DESIGN_W - 58, y));
      const referenceY = y + 34;
      const selected = this.selectedReference?.type === this.selectedType ? this.selectedReference : null;
      const referenceField = new TicketReferenceField({
        width: w,
        height: 96,
        text: selected?.label ?? this.referencePlaceholder(references.length),
        accessibleLabel: selected
          ? `مورد مرتبط انتخاب شده: ${selected.label}. برای تغییر لمس کنید`
          : this.referencePlaceholder(references.length),
        enabled: references.length > 0,
        selected: Boolean(selected),
        onTap: () => this.openReferencePicker(),
      });
      referenceField.position.set(left, referenceY);
      form.addChild(referenceField);
      y = referenceY + 114;
    }

    form.addChild(fieldLabel('موضوع', DESIGN_W - 58, y));
    const subjectY = y + 34;
    this.subjectField = new TextField({
      width: w,
      height: 74,
      value: subjectValue,
      placeholder: 'موضوع پیام را بنویسید',
      accessibilityLabel: 'موضوع پیام پشتیبانی',
      enterKeyHint: 'next',
    });
    this.subjectField.position.set(left, subjectY);
    form.addChild(this.subjectField);
    y = subjectY + 92;

    form.addChild(fieldLabel('متن پیام', DESIGN_W - 58, y));
    const messageY = y + 34;
    const messageH = computeTicketMessageLayout(viewport.h, messageY).height;
    this.bodyField = new TextArea({
      width: w,
      height: messageH,
      value: bodyValue,
      placeholder: 'مشکل یا درخواست خود را با جزئیات بنویسید…',
      accessibilityLabel: 'متن پیام پشتیبانی',
      enterKeyHint: 'done',
    });
    this.bodyField.position.set(left, messageY);
    form.addChild(this.bodyField);
    this.subjectField.onKeyboardEnter(() => this.bodyField?.focus());

    const formBottom = messageY + messageH + 18;
    const card = glassCard(24, formTop - 14, DESIGN_W - 48, formBottom - formTop + 14);
    this.body.addChild(card, form);
    animatedNodes.push(card, form);

    const send = new Button(existing ? 'ثبت تغییرات تیکت' : 'ارسال تیکت', () => this.send(), {
      width: w,
      height: 80,
      fontSize: 28,
      icon: this.ctx.iconTexture('ticket'),
      accessibleLabel: existing ? 'ثبت تغییرات تیکت پشتیبانی' : 'ارسال تیکت برای پشتیبانی',
    });
    bottomBar([send]);
    this.body.addChild(send);
    animatedNodes.push(send);

    if (!this.entered) {
      this.entered = true;
      enterFade(animatedNodes, 35);
    }
    if (this.formDialogOpen) this.body.visible = false;
  }

  private destroyBody(): void {
    for (const child of this.body.removeChildren()) child.destroy({ children: true });
    this.subjectField = undefined;
    this.bodyField = undefined;
  }

  private chooseType(type: TicketType): void {
    if (type === this.selectedType) return;
    this.selectedType = type;
    this.selectedReference = null;
    this.build();
  }

  private openReferencePicker(): void {
    const state = this.state;
    if (!state || this.selectedType === TicketType.General || this.referencePicker) return;
    const options = ticketReferenceOptions(state, this.selectedType);
    if (!options.length) return;

    this.subjectField?.blur();
    this.bodyField?.blur();
    this.body.visible = false;
    this.referencePicker = new TicketReferencePicker(
      this.referencePickerTitle(),
      options,
      this.selectedReference?.type === this.selectedType ? this.selectedReference.sourceIndex : null,
      (option) => {
        this.selectedReference = option;
        this.closeReferencePicker();
        this.build();
      },
      () => this.closeReferencePicker(),
    );
    this.addChild(this.referencePicker);
  }

  private closeReferencePicker(): void {
    if (this.referencePicker) {
      this.removeChild(this.referencePicker);
      this.referencePicker.destroy({ children: true });
      this.referencePicker = undefined;
    }
    this.body.visible = true;
  }

  private referencePickerTitle(): string {
    if (this.selectedType === TicketType.Game) return 'انتخاب بازی';
    if (this.selectedType === TicketType.Transaction) return 'انتخاب تراکنش';
    return 'انتخاب برداشت';
  }

  private referencePlaceholder(count: number): string {
    if (!count) {
      if (this.selectedType === TicketType.Game) return 'بازی اخیری برای انتخاب وجود ندارد';
      if (this.selectedType === TicketType.Transaction) return 'تراکنش اخیری برای انتخاب وجود ندارد';
      return 'برداشت اخیری برای انتخاب وجود ندارد';
    }
    if (this.selectedType === TicketType.Game) return 'برای انتخاب بازی مرتبط لمس کنید';
    if (this.selectedType === TicketType.Transaction) return 'برای انتخاب تراکنش مرتبط لمس کنید';
    return 'برای انتخاب برداشت مرتبط لمس کنید';
  }

  private sameReference(a: TicketReferenceOption, b: TicketReferenceOption): boolean {
    return a.type === b.type && a.sourceIndex === b.sourceIndex && a.referenceId === b.referenceId;
  }

  private statusCard(status: number, width: number): Container {
    const card = new Container();
    card.addChild(new Panel(width, 64, { fill: COLORS.panelSoft, border: true }));
    const dot = new Graphics().circle(width - 28, 32, 8).fill({ color: this.statusColor(status) });
    const text = makeText(`وضعیت تیکت: ${STATUS_LABELS[status] ?? 'نامشخص'}`, {
      size: 24,
      weight: '700',
      fill: this.statusColor(status),
    });
    text.anchor.set(1, 0.5);
    text.position.set(width - 48, 32);
    card.addChild(text, dot);
    return card;
  }

  private statusColor(status: number): number {
    if (status === TicketStatus.Done) return 0x4ade80;
    if (status === TicketStatus.InProgress) return COLORS.gold;
    if (status === TicketStatus.Offensive) return COLORS.danger;
    return COLORS.text;
  }

  /** Pixi dialogs sit below DOM editors, so hide the form while a dialog is open. */
  private showFormDialog(opts: { title?: string; message: string; onClose?: () => void }): void {
    this.formDialogOpen = true;
    this.subjectField?.blur();
    this.bodyField?.blur();
    this.body.visible = false;
    this.ctx.dialog({
      title: opts.title,
      message: opts.message,
      onClose: () => {
        this.formDialogOpen = false;
        if (this.parent) this.body.visible = true;
        opts.onClose?.();
      },
    });
  }

  private send(): void {
    const state = this.state;
    if (!state || !this.subjectField || !this.bodyField) return;
    const references = ticketReferenceOptions(state, this.selectedType);
    const error = validateTicketDraft(
      this.subjectField.value,
      this.bodyField.value,
      this.selectedType,
      references.length,
      this.selectedReference,
    );

    if (error === 'subject') {
      this.showFormDialog({ message: 'لطفاً موضوع پیام را وارد کنید.', onClose: () => this.subjectField?.focus() });
      return;
    }
    if (error === 'body') {
      this.showFormDialog({ message: 'لطفاً متن پیام را وارد کنید.', onClose: () => this.bodyField?.focus() });
      return;
    }
    if (error === 'reference') {
      this.showFormDialog({
        message: 'لطفاً مورد مرتبط با تیکت را انتخاب کنید.',
        onClose: () => this.openReferencePicker(),
      });
      return;
    }

    this.ctx.client.send(
      encodeTicketSave(
        this.subjectField.value.trim(),
        this.bodyField.value.trim(),
        this.selectedType,
        this.selectedReference?.type === this.selectedType ? this.selectedReference.referenceId : 0n,
      ),
    );
    this.ctx.navigate(RoomName.MainMenu);
  }
}
